import { useState, useCallback, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  getPersonalPromptTemplates,
  createPromptTemplate,
  updatePromptTemplate,
  deletePromptTemplate,
  getCategories,
} from '../../api';
import type { PromptTemplate, PromptTemplateCategory } from '../../api';
import { hasSystemPromptCategory } from '../../utils/systemPromptCategory';
import { TemplateFormModal, DeleteConfirmDialog } from '../index';
import styles from './MyPrompts.module.scss';

interface MyPromptsProps {
  onUsePrompt: (content: string) => void;
  onApplyToAiSettings?: (content: string) => void;
}

export default function MyPrompts({ onUsePrompt, onApplyToAiSettings }: MyPromptsProps) {
  const [prompts, setPrompts] = useState<PromptTemplate[]>([]);
  const [categories, setCategories] = useState<PromptTemplateCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PromptTemplate | null>(null);

  const loadPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPersonalPromptTemplates();
      setPrompts(res.items ?? []);
    } catch {
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    const data = await getCategories();
    setCategories(data);
  }, []);

  useEffect(() => {
    loadPrompts();
    loadCategories();
  }, [loadPrompts, loadCategories]);

  const handleTemplateSubmit = useCallback(
    async (form: {
      title: string;
      description?: string;
      content: string;
      category_ids: string[];
    }) => {
      if (editingTemplate) {
        await updatePromptTemplate(editingTemplate.id, form);
      } else {
        await createPromptTemplate(form);
      }
      await loadPrompts();
      setTemplateModalOpen(false);
      setEditingTemplate(null);
    },
    [editingTemplate, loadPrompts]
  );

  const handleDeleteTemplate = useCallback(async () => {
    if (!deleteTarget) return;
    await deletePromptTemplate(deleteTarget.id);
    await loadPrompts();
    setDeleteTarget(null);
  }, [deleteTarget, loadPrompts]);

  const handleAdd = useCallback(() => {
    setEditingTemplate(null);
    setTemplateModalOpen(true);
  }, []);

  const handleEdit = useCallback((prompt: PromptTemplate) => {
    setEditingTemplate(prompt);
    setTemplateModalOpen(true);
  }, []);

  const handleDelete = useCallback((prompt: PromptTemplate) => {
    setDeleteTarget(prompt);
  }, []);

  if (loading) {
    return (
      <section className={styles.root}>
        <div className={styles.loading}>Загрузка…</div>
      </section>
    );
  }

  return (
    <section className={styles.root}>
      <div className={styles.toolbarRow}>
        <button type="button" className={styles.addButton} onClick={handleAdd}>
          <AddIcon className={styles.addIcon} />
          Добавить промпт
        </button>
      </div>

      <div className={styles.list}>
        {prompts.length === 0 ? (
          <div className={styles.empty}>
            <p>У вас пока нет своих шаблонов</p>
            <p className={styles.emptyHint}>Нажмите «Добавить промпт», чтобы создать первый</p>
          </div>
        ) : (
          prompts.map((item) => (
            <div key={item.id} className={styles.item}>
              <div className={styles.itemContent}>
                <h3 className={styles.itemTitle}>{item.title}</h3>
                {item.categories?.length ? (
                  <div className={styles.itemCategories}>
                    {item.categories.map((c) => (
                      <span
                        key={c.id}
                        className={styles.itemCategory}
                        style={{ backgroundColor: c.color, color: '#fff' }}
                      >
                        {c.title}
                      </span>
                    ))}
                  </div>
                ) : null}
                <p className={styles.itemPreview}>
                  {item.content.length > 120 ? `${item.content.slice(0, 120)}…` : item.content}
                </p>
              </div>
              <div className={styles.itemActions}>
                <button
                  type="button"
                  className={styles.useButton}
                  onClick={() => onUsePrompt(item.content)}
                >
                  Использовать
                  <ArrowForwardIcon className={styles.buttonIcon} />
                </button>
                {onApplyToAiSettings && hasSystemPromptCategory(item.categories) && (
                  <button
                    type="button"
                    className={styles.aiSettingsButton}
                    onClick={() => onApplyToAiSettings(item.content)}
                    title="Подставить в системный промпт на странице «Настройки ИИ»"
                  >
                    В настройки ИИ
                    <SettingsOutlinedIcon className={styles.buttonIcon} />
                  </button>
                )}
                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={() => handleEdit(item)}
                  title="Редактировать"
                >
                  <EditIcon fontSize="small" />
                </button>
                <button
                  type="button"
                  className={`${styles.iconButton} ${styles.deleteButton}`}
                  onClick={() => handleDelete(item)}
                  title="Удалить"
                >
                  <DeleteIcon fontSize="small" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <TemplateFormModal
        open={templateModalOpen}
        onClose={() => {
          setTemplateModalOpen(false);
          setEditingTemplate(null);
        }}
        onSubmit={handleTemplateSubmit}
        template={editingTemplate}
        categories={categories}
      />

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteTemplate}
        title="Удалить шаблон?"
        message={deleteTarget ? `Шаблон «${deleteTarget.title}» будет удалён.` : ''}
      />
    </section>
  );
}
